import { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

function InvestorProof() {
  const [showAdmin, setShowAdmin] = useState(false);
  const [startAmount, setStartAmount] = useState(8000);
  const [riskPct, setRiskPct] = useState(10);
  const [adminNote, setAdminNote] = useState("Nov 7, 2025 — System made R12,350 today on NAS100-A.");
  const [projectionData, setProjectionData] = useState([]);

  useEffect(() => {
    // Check for admin query param
    const params = new URLSearchParams(window.location.search);
    if (params.get('admin') === '1') {
      setShowAdmin(true);
    }
  }, []);

  useEffect(() => {
    generateProjection();
  }, [startAmount, riskPct]);

  const generateProjection = () => {
    const days = 14;
    let bal = startAmount;
    const proj = [];
    for (let i = 0; i < days; i++) {
      const p = (riskPct / 100) * (Math.random() * 2.0 - 0.3);
      bal = Math.max(0, bal * (1 + p));
      proj.push(Math.round(bal));
    }
    setProjectionData(proj);
  };

  // Generate demo data
  const labels = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (13 - i));
    return d.toISOString().slice(5, 10);
  });

  const equitySeries = labels.map((_, i) => startAmount + i * (800 + Math.random() * 300));
  const plSeries = labels.map(() => (Math.random() - 0.2) * 2500);
  const projLabels = Array.from({ length: 14 }, (_, i) => `D${i}`);

  const equityChartData = {
    labels,
    datasets: [{
      label: 'Equity (R)',
      data: equitySeries,
      borderColor: '#00FF7F',
      backgroundColor: 'rgba(0, 255, 127, 0.1)',
      tension: 0.35
    }]
  };

  const plChartData = {
    labels,
    datasets: [{
      label: 'Daily P/L (R)',
      data: plSeries,
      backgroundColor: plSeries.map(v => v >= 0 ? '#00FF7F' : '#ef4444')
    }]
  };

  const projectionChartData = {
    labels: projLabels,
    datasets: [{
      label: 'Projection (R)',
      data: projectionData,
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      tension: 0.35
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#fff' }
      }
    },
    scales: {
      x: {
        ticks: { color: '#9aa4ad' },
        grid: { color: 'rgba(255,255,255,0.05)' }
      },
      y: {
        ticks: { color: '#9aa4ad' },
        grid: { color: 'rgba(255,255,255,0.05)' }
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f12] text-white py-20">
      <section className="max-w-6xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => window.history.back()}
          className="mb-6 flex items-center gap-2 px-4 py-2 text-sm font-light text-gray-400 hover:text-white border border-zinc-800 hover:border-zinc-700 rounded-xl transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-3 h-3 rounded-full bg-[#00FF7F] animate-pulse"></div>
          <h2 className="text-2xl md:text-3xl font-light tracking-tight">Kasango AI Pro Results Dashboard</h2>
        </div>

        {/* KPI cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs uppercase text-white/60 font-extralight">Starting Amount</div>
            <div className="text-2xl font-light">R{startAmount.toLocaleString()}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs uppercase text-white/60 font-extralight">Risk Level</div>
            <div className="text-2xl font-light">{riskPct <= 5 ? 'Low' : riskPct <= 10 ? 'Medium' : 'High'} ({riskPct}%)</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 col-span-2">
            <div className="text-xs uppercase text-white/60 font-extralight">Admin Note of the Day</div>
            <div className="mt-2 text-sm font-light">{adminNote}</div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="font-light mb-4">Equity Growth</div>
            <div style={{ height: '260px' }}>
              <Line data={equityChartData} options={chartOptions} />
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="font-light mb-4">Daily Profit/Loss</div>
            <div style={{ height: '260px' }}>
              <Bar data={plChartData} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* Mockup projection */}
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-6">
          <div className="font-light mb-4">Mockup Projection (Start Amount × Risk)</div>
          <div style={{ height: '260px' }}>
            <Line data={projectionChartData} options={chartOptions} />
          </div>
          <p className="text-xs text-white/60 mt-4">This projection is illustrative. Past performance is not indicative of future results.</p>
        </div>

        {/* Kasango AI Live Charts Section */}
        <div className="mt-8 mb-6">
          <h3 className="text-2xl font-light mb-4 flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-[#00FF7F] animate-pulse"></div>
            Kasango AI Pro — Live Chart View
          </h3>
          <p className="text-sm text-gray-400 font-extralight mb-6">Watch our AI expert advisor in action on real market data</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Gold (XAUUSD) Chart */}
          <div className="rounded-xl border border-[#00FF7F]/20 bg-zinc-950 overflow-hidden">
            <div className="bg-zinc-900 px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h4 className="font-light text-sm">XAUUSD</h4>
                <p className="text-xs text-gray-500 font-extralight">Gold / US Dollar</p>
              </div>
              <div className="text-right">
                <div className="text-[#00FF7F] text-lg font-light">2,645.80</div>
                <div className="text-xs text-[#00FF7F]">+12.40 (+0.47%)</div>
              </div>
            </div>
            <div className="p-4 relative">
              <div className="relative h-64 bg-black/40 rounded overflow-hidden">
                {/* Chart Lines - Mockup */}
                <svg className="w-full h-full" viewBox="0 0 400 256" preserveAspectRatio="none">
                  {/* Grid */}
                  <line x1="0" y1="64" x2="400" y2="64" stroke="#1f2937" strokeWidth="1" />
                  <line x1="0" y1="128" x2="400" y2="128" stroke="#1f2937" strokeWidth="1" />
                  <line x1="0" y1="192" x2="400" y2="192" stroke="#1f2937" strokeWidth="1" />
                  
                  {/* Price Line */}
                  <path 
                    d="M0,180 L50,160 L100,140 L150,120 L200,100 L250,110 L300,95 L350,85 L400,80" 
                    fill="none" 
                    stroke="#00FF7F" 
                    strokeWidth="2"
                  />
                  
                  {/* BUY Signal 1 */}
                  <circle cx="100" cy="140" r="8" fill="#00FF7F" opacity="0.3" />
                  <circle cx="100" cy="140" r="4" fill="#00FF7F" />
                  <text x="100" y="170" fill="#00FF7F" fontSize="10" textAnchor="middle" fontWeight="bold">BUY</text>
                  
                  {/* BUY Signal 2 */}
                  <circle cx="250" cy="110" r="8" fill="#00FF7F" opacity="0.3" />
                  <circle cx="250" cy="110" r="4" fill="#00FF7F" />
                  <text x="250" y="140" fill="#00FF7F" fontSize="10" textAnchor="middle" fontWeight="bold">BUY</text>
                  
                  {/* SELL Signal */}
                  <circle cx="350" cy="85" r="8" fill="#ef4444" opacity="0.3" />
                  <circle cx="350" cy="85" r="4" fill="#ef4444" />
                  <text x="350" y="70" fill="#ef4444" fontSize="10" textAnchor="middle" fontWeight="bold">SELL</text>
                </svg>
                
                {/* EA Indicator Badge */}
                <div className="absolute top-2 left-2 px-3 py-1 bg-[#00FF7F]/20 border border-[#00FF7F]/40 rounded-full backdrop-blur-sm">
                  <span className="text-[10px] text-[#00FF7F] font-light tracking-wider">KASANGO AI • ACTIVE</span>
                </div>
                
                {/* Stats */}
                <div className="absolute bottom-2 left-2 right-2 flex gap-2">
                  <div className="flex-1 bg-black/60 backdrop-blur-sm border border-zinc-800 rounded px-2 py-1">
                    <div className="text-[9px] text-gray-500">Status</div>
                    <div className="text-xs text-[#00FF7F] font-light">Active</div>
                  </div>
                  <div className="flex-1 bg-black/60 backdrop-blur-sm border border-zinc-800 rounded px-2 py-1">
                    <div className="text-[9px] text-gray-500">Signals</div>
                    <div className="text-xs text-white font-light">124 today</div>
                  </div>
                  <div className="flex-1 bg-black/60 backdrop-blur-sm border border-zinc-800 rounded px-2 py-1">
                    <div className="text-[9px] text-gray-500">Profit</div>
                    <div className="text-xs text-[#00FF7F] font-light">+R8,450</div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3 font-extralight">Live EA signals • Auto-trading enabled • Risk: Medium</p>
            </div>
          </div>

          {/* NASDAQ (NAS100) Chart */}
          <div className="rounded-xl border border-[#00FF7F]/20 bg-zinc-950 overflow-hidden">
            <div className="bg-zinc-900 px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h4 className="font-light text-sm">NAS100</h4>
                <p className="text-xs text-gray-500 font-extralight">NASDAQ 100 Index</p>
              </div>
              <div className="text-right">
                <div className="text-[#00FF7F] text-lg font-light">20,245.60</div>
                <div className="text-xs text-[#00FF7F]">+185.30 (+0.92%)</div>
              </div>
            </div>
            <div className="p-4 relative">
              <div className="relative h-64 bg-black/40 rounded overflow-hidden">
                {/* Chart Lines - Mockup */}
                <svg className="w-full h-full" viewBox="0 0 400 256" preserveAspectRatio="none">
                  {/* Grid */}
                  <line x1="0" y1="64" x2="400" y2="64" stroke="#1f2937" strokeWidth="1" />
                  <line x1="0" y1="128" x2="400" y2="128" stroke="#1f2937" strokeWidth="1" />
                  <line x1="0" y1="192" x2="400" y2="192" stroke="#1f2937" strokeWidth="1" />
                  
                  {/* Price Line */}
                  <path 
                    d="M0,200 L50,190 L100,170 L150,160 L200,150 L250,130 L300,140 L350,120 L400,110" 
                    fill="none" 
                    stroke="#00FF7F" 
                    strokeWidth="2"
                  />
                  
                  {/* BUY Signal 1 */}
                  <circle cx="150" cy="160" r="8" fill="#00FF7F" opacity="0.3" />
                  <circle cx="150" cy="160" r="4" fill="#00FF7F" />
                  <text x="150" y="190" fill="#00FF7F" fontSize="10" textAnchor="middle" fontWeight="bold">BUY</text>
                  
                  {/* SELL Signal 1 */}
                  <circle cx="200" cy="150" r="8" fill="#ef4444" opacity="0.3" />
                  <circle cx="200" cy="150" r="4" fill="#ef4444" />
                  <text x="200" y="135" fill="#ef4444" fontSize="10" textAnchor="middle" fontWeight="bold">SELL</text>
                  
                  {/* BUY Signal 2 */}
                  <circle cx="300" cy="140" r="8" fill="#00FF7F" opacity="0.3" />
                  <circle cx="300" cy="140" r="4" fill="#00FF7F" />
                  <text x="300" y="170" fill="#00FF7F" fontSize="10" textAnchor="middle" fontWeight="bold">BUY</text>
                </svg>
                
                {/* EA Indicator Badge */}
                <div className="absolute top-2 left-2 px-3 py-1 bg-[#00FF7F]/20 border border-[#00FF7F]/40 rounded-full backdrop-blur-sm">
                  <span className="text-[10px] text-[#00FF7F] font-light tracking-wider">KASANGO AI • ACTIVE</span>
                </div>
                
                {/* Stats */}
                <div className="absolute bottom-2 left-2 right-2 flex gap-2">
                  <div className="flex-1 bg-black/60 backdrop-blur-sm border border-zinc-800 rounded px-2 py-1">
                    <div className="text-[9px] text-gray-500">Status</div>
                    <div className="text-xs text-[#00FF7F] font-light">Active</div>
                  </div>
                  <div className="flex-1 bg-black/60 backdrop-blur-sm border border-zinc-800 rounded px-2 py-1">
                    <div className="text-[9px] text-gray-500">Signals</div>
                    <div className="text-xs text-white font-light">98 today</div>
                  </div>
                  <div className="flex-1 bg-black/60 backdrop-blur-sm border border-zinc-800 rounded px-2 py-1">
                    <div className="text-[9px] text-gray-500">Profit</div>
                    <div className="text-xs text-[#00FF7F] font-light">+R6,720</div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3 font-extralight">Live EA signals • Auto-trading enabled • Risk: Medium</p>
            </div>
          </div>
        </div>

        {/* Additional Trading Pairs Info */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#00FF7F]"></div>
              <span className="text-xs text-gray-400 font-extralight">XAUUSD</span>
            </div>
            <div className="text-sm font-light">Gold</div>
            <div className="text-xs text-[#00FF7F] mt-1">Active</div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#00FF7F]"></div>
              <span className="text-xs text-gray-400 font-extralight">NAS100</span>
            </div>
            <div className="text-sm font-light">NASDAQ</div>
            <div className="text-xs text-[#00FF7F] mt-1">Active</div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#00FF7F]"></div>
              <span className="text-xs text-gray-400 font-extralight">GER30</span>
            </div>
            <div className="text-sm font-light">DAX</div>
            <div className="text-xs text-[#00FF7F] mt-1">Active</div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#00FF7F]"></div>
              <span className="text-xs text-gray-400 font-extralight">SPX500</span>
            </div>
            <div className="text-sm font-light">S&P 500</div>
            <div className="text-xs text-[#00FF7F] mt-1">Active</div>
          </div>
        </div>

        {/* Admin Controls */}
        {showAdmin && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5">
            <h4 className="font-light mb-4 text-lg">Admin Controls</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <label className="block text-sm">
                Starting Amount
                <input
                  type="number"
                  value={startAmount}
                  onChange={(e) => setStartAmount(Number(e.target.value))}
                  className="mt-2 w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white"
                />
              </label>
              <label className="block text-sm">
                Risk (% per trade)
                <input
                  type="number"
                  value={riskPct}
                  onChange={(e) => setRiskPct(Number(e.target.value))}
                  className="mt-2 w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white"
                />
              </label>
              <label className="block text-sm">
                Admin Note
                <input
                  type="text"
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  className="mt-2 w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white"
                />
              </label>
            </div>
            <p className="text-xs text-white/60 mt-4">Tip: move these to a real backend later.</p>
          </div>
        )}

        <p className="text-[11px] text-white/50 mt-8">Trading involves risk. This page is for transparency & education.</p>
      </section>
    </div>
  );
}

export default InvestorProof;
